import type { I18nText } from '../dto/survey/survey-schema.types';

export type SurveyAnagraficaSourceId =
  | 'tenant.ragioneSociale'
  | 'tenant.name'
  | 'tenant.partitaIva'
  | 'tenant.codiceFiscale'
  | 'tenant.indirizzoVia'
  | 'tenant.indirizzoCap'
  | 'tenant.indirizzoCitta'
  | 'tenant.indirizzoProvincia'
  | 'tenant.regione'
  | 'tenant.pec'
  | 'tenant.phone'
  | 'tenant.establishmentAddress'
  | 'tenant.establishmentApprovalNumber'
  | 'owner.fullName'
  | 'owner.firstName'
  | 'owner.lastName'
  | 'owner.email'
  | 'tenant.haccpResponsibleName'
  | 'currentUser.fullName';

export type SurveyAnagraficaNodeType = 'text' | 'textarea' | 'single-choice';

export interface SurveyAnagraficaSource {
  id: SurveyAnagraficaSourceId;
  label: I18nText; // shown in back-office select
  hint?: I18nText;
  sensitive?: boolean; // PII gated at resolution time
}

export const SURVEY_ANAGRAFICA_SOURCES: readonly SurveyAnagraficaSource[] = [
  {
    id: 'tenant.ragioneSociale',
    label: { it: 'Ragione sociale', en: 'Business name' },
    hint: {
      it: 'Ragione sociale, o nome workspace se assente',
      en: 'Business name, or workspace name if missing',
    },
  },
  {
    id: 'tenant.name',
    label: { it: 'Nome attività (workspace)', en: 'Business name (workspace)' },
  },
  {
    id: 'tenant.partitaIva',
    label: { it: 'Partita IVA', en: 'VAT number' },
  },
  {
    id: 'tenant.codiceFiscale',
    label: { it: 'Codice fiscale', en: 'Tax code' },
  },
  {
    id: 'tenant.indirizzoVia',
    label: { it: 'Indirizzo (via)', en: 'Street address' },
  },
  {
    id: 'tenant.indirizzoCap',
    label: { it: 'CAP', en: 'Postal code' },
  },
  {
    id: 'tenant.indirizzoCitta',
    label: { it: 'Comune', en: 'City' },
  },
  {
    id: 'tenant.indirizzoProvincia',
    label: { it: 'Provincia', en: 'Province' },
  },
  {
    id: 'tenant.regione',
    label: { it: 'Regione (da provincia)', en: 'Region (from province)' },
    hint: {
      it: "Derivata dalla provincia dell'attività",
      en: 'Derived from the business province',
    },
  },
  {
    id: 'tenant.pec',
    label: { it: 'PEC', en: 'PEC (certified email)' },
    sensitive: true,
  },
  {
    id: 'tenant.phone',
    label: { it: 'Telefono attività', en: 'Business phone' },
    sensitive: true,
  },
  {
    id: 'tenant.establishmentAddress',
    label: { it: 'Indirizzo stabilimento', en: 'Establishment address' },
  },
  {
    id: 'tenant.establishmentApprovalNumber',
    label: { it: 'Numero riconoscimento stabilimento', en: 'Establishment approval number' },
  },
  {
    id: 'owner.fullName',
    label: { it: 'Nome e cognome titolare', en: 'Owner full name' },
  },
  {
    id: 'owner.firstName',
    label: { it: 'Nome titolare', en: 'Owner first name' },
  },
  {
    id: 'owner.lastName',
    label: { it: 'Cognome titolare', en: 'Owner last name' },
  },
  {
    id: 'owner.email',
    label: { it: 'Email titolare', en: 'Owner email' },
    sensitive: true,
  },
  {
    id: 'tenant.haccpResponsibleName',
    label: { it: 'Responsabile HACCP', en: 'HACCP responsible' },
    // Round 16 (decision 9): NO fallback to the owner any more — prefilled
    // only when the tenant has an actual designated responsible. Pair with
    // `suggestedValuesSource: 'tenant.haccpResponsibleCandidates'` (a
    // `TextNode`-only, non-`defaultValueSource` field) to propose the owner
    // and the connected user as tap-to-fill suggestions when there is none.
    hint: {
      it: 'Compilato solo se il responsabile HACCP è configurato — nessun titolare sostitutivo',
      en: 'Filled in only when the HACCP responsible is configured — no owner substitute',
    },
  },
  {
    id: 'currentUser.fullName',
    label: { it: 'Nome e cognome di chi compila', en: 'Full name of the person filling in' },
    hint: {
      it: "Utente attualmente autenticato, non il titolare dell'attività",
      en: 'Currently authenticated user, not the business owner',
    },
  },
];

export const SURVEY_DEFAULT_SOURCE_NODE_TYPES: readonly SurveyAnagraficaNodeType[] = [
  'text',
  'textarea',
  'single-choice',
];

const SOURCE_BY_ID = new Map<string, SurveyAnagraficaSource>(
  SURVEY_ANAGRAFICA_SOURCES.map((s) => [s.id, s]),
);

export function getSurveyAnagraficaSource(id: string): SurveyAnagraficaSource | undefined {
  return SOURCE_BY_ID.get(id);
}
